import React from 'react';

import classNames from 'clsx';
import { Helmet } from 'react-helmet';

import { Alert, Box, Button, GridLegacy as Grid, Typography } from '@mui/material';

import * as libRegions from '@luxuryescapes/lib-regions';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';

import { LE_AGENT_HUB, LE_AGENT_HUB_CVFR, brands } from '~/consts/brands';
import { LE_BUSINESS_TRAVELLER } from '~/consts/businessTraveller';

import { getAllScheduledRegions } from '~/utils/getAllScheduledRegions';

import isE2ETestOffer from '../../../utils/isE2ETestOffer';

import NewLEDSchedules from './LEDSchedules';
import OldLESchedules from './LESchedulesOld/NewScheduleFrontPage';
import OldLEDSchedules from './NewScheduleByBrand';
import NewScheduleByType from './NewScheduleByType';
import NewSchedules from './common/Schedules';

const dataMigratingText = 'Updating...';
const dataMigrationText = 'Clear Schedules';

const getMigrationButtonText = (isMigratingData, migrationError) => {
  if (migrationError) {
    return migrationError;
  }
  if (isMigratingData) {
    return dataMigratingText;
  }
  return dataMigrationText;
};

export default function NewSchedulePageEdit({
  offerId,
  offerName,
  schedules,
  shouldMigrateSchedule,
  migrateScheduleData,
  isMigratingData,
  migrationError,
  migrationButtonState,
}) {
  const brandsValue = brands.map((brand) => brand.value);
  const otherBrands = [...brands].slice(1);
  const regions = libRegions.getRegions();
  const enabledRegions = regions.filter((r) => !getAllScheduledRegions(schedules).includes(r.code));
  const showE2EOfferWarning = isE2ETestOffer(offerName);

  return (
    <Box>
      <Helmet>
        <title>Offers | {offerName} | Edit Schedule</title>
      </Helmet>
      <Box px={5} pt={3}>
        <OfferBreadcrumbs offerId={offerId} currentPage="Schedules" />
      </Box>
      <Grid container direction="row" alignContent="space-between" p={5} pt={2}>
        <Grid item xs />
        <Grid item>
          <Button
            variant="contained"
            className={classNames(migrationButtonState)}
            onClick={migrateScheduleData}
            disabled={!shouldMigrateSchedule || isMigratingData}
          >
            {getMigrationButtonText(isMigratingData, migrationError)}
          </Button>
        </Grid>
      </Grid>
      {showE2EOfferWarning && (
        <Box p={5}>
          <Alert icon={false} variant="filled" severity="error" sx={{ p: 4 }}>
            <Typography variant="h3">This is an E2E test offer</Typography>
            <Typography variant="body1">
              Changing this offer's schedules so that it doesn't appear on the homepage may break the E2E tests. Only
              change these if you know what you're doing!
            </Typography>
          </Alert>
        </Box>
      )}
      {shouldMigrateSchedule && (
        <Grid container item xs={12} px={5}>
          <Alert severity="warning" sx={{ width: '100%' }}>
            This offer has invalid schedules that are not in the new format or missing some brands. Please clear
            schedules and re-add them to fix this.
          </Alert>
        </Grid>
      )}
      <NewScheduleByType
        schedules={schedules}
        brandsValue={brandsValue}
        offerId={offerId}
        regions={regions}
        shouldMigrateSchedule={shouldMigrateSchedule}
      />
      {window.configs.DISTRIBUTION_CONTROLS_ENABLED === 'on' ? (
        <>
          <NewSchedules brand="luxuryescapes" offerId={offerId} shouldMigrateSchedule={shouldMigrateSchedule} />
          <NewSchedules brand={LE_BUSINESS_TRAVELLER} offerId={offerId} shouldMigrateSchedule={shouldMigrateSchedule} />
          <NewSchedules
            brand={LE_AGENT_HUB}
            affectedBrands={[LE_AGENT_HUB, LE_AGENT_HUB_CVFR]}
            offerId={offerId}
            shouldMigrateSchedule={shouldMigrateSchedule}
          />
          <NewLEDSchedules offerId={offerId} shouldMigrateSchedule={shouldMigrateSchedule} />
        </>
      ) : (
        <>
          <OldLESchedules
            offerId={offerId}
            enabledRegions={enabledRegions}
            shouldMigrateSchedule={shouldMigrateSchedule}
          />
          <OldLEDSchedules
            schedules={schedules}
            brands={otherBrands}
            offerId={offerId}
            regions={regions}
            shouldMigrateSchedule={shouldMigrateSchedule}
          />
        </>
      )}
    </Box>
  );
}
